import { useEffect, useState } from 'react'
import { APP_ENV, isProduction, supabaseLabel } from '../lib/env'
import { APP_VERSION } from '../lib/changelog'

/**
 * A corner pill naming the environment, plus a tap-to-open panel with the
 * details you actually need when something looks wrong: which build, which
 * Supabase project, which service worker.
 *
 * Renders nothing in production. Three environments that look identical is a
 * good way to test against the family's real list by accident, or to spend ten
 * minutes debugging a staging bug in a local tab.
 *
 * Also reachable in production with ?debug — same panel, no pill — for the
 * "what version is this phone actually running?" question that the PWA's
 * user-accepted update model makes genuinely hard to answer otherwise.
 */
export function EnvBadge() {
  const [open, setOpen] = useState(false)
  // Read during the initial render rather than in an effect — the query string
  // is available synchronously and never changes for the life of the component,
  // so an effect would only cost a second render.
  const [forced, setForced] = useState(
    () => new URLSearchParams(window.location.search).has('debug'),
  )
  const supported = 'serviceWorker' in navigator
  const [sw, setSw] = useState(supported ? 'checking…' : 'unsupported')

  useEffect(() => {
    if (!open || !supported) return
    // Only the async result lands in state; nothing is set synchronously here.
    navigator.serviceWorker.getRegistration()
      .then(reg => {
        if (!reg) { setSw('none registered'); return }
        setSw(reg.waiting ? 'update waiting' : reg.active ? 'active' : 'installing')
      })
      .catch(() => setSw('unavailable'))
  }, [open, supported])

  if (isProduction && !forced) return null

  const color = APP_ENV === 'local' ? 'var(--ml-env-local)' : APP_ENV === 'staging' ? 'var(--ml-env-staging)' : 'var(--ml-env-other)'

  return (
    <>
      {!isProduction && (
        <button
          onClick={() => setOpen(o => !o)}
          aria-label={`Environment: ${APP_ENV}. Tap for build details.`}
          style={{
            position: 'fixed', bottom: 'calc(8px + env(safe-area-inset-bottom))', left: 8,
            zIndex: 9999, padding: '3px 8px', borderRadius: 999, border: 'none',
            background: color, color: 'var(--ml-on-delete)', font: '600 10px/1.4 var(--ml-font-mono)',
            letterSpacing: '.06em', textTransform: 'uppercase', opacity: 0.85, cursor: 'pointer',
          }}
        >
          {APP_ENV}
        </button>
      )}

      {(open || (forced && isProduction)) && (
        <div
          role="dialog"
          aria-label="Build details"
          onClick={() => { setOpen(false); setForced(false) }}
          style={{
            position: 'fixed', inset: 'auto 8px calc(36px + env(safe-area-inset-bottom)) 8px',
            zIndex: 9999, padding: 12, borderRadius: 10,
            background: 'var(--ml-debug-bg)', color: 'var(--ml-debug-fg)',
            font: '11px/1.7 var(--ml-font-mono)', maxWidth: 380,
          }}
        >
          <Row label="env" value={APP_ENV} />
          <Row label="version" value={APP_VERSION} />
          <Row label="supabase" value={supabaseLabel()} />
          <Row label="worker" value={sw} />
          <Row label="mode" value={import.meta.env.MODE} />
          <div style={{ opacity: 0.5, marginTop: 6 }}>tap to dismiss</div>
        </div>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <span style={{ opacity: 0.55, minWidth: 66 }}>{label}</span>
      <span style={{ wordBreak: 'break-all' }}>{value}</span>
    </div>
  )
}
